import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Book } from '@booknest/shared';

import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { pickColors } from '@/features/library/components/bookcase/spine-utils.js';

import { useMyCollections } from '../hooks/useMyCollections.js';

import { CollectionFormModal } from './CollectionFormModal.js';

/**
 * Section "Mes collections" — row de cartes au-dessus du toolbar de la page
 * Étagère. Chaque carte montre un aperçu des tranches sur fond bois, puis
 * nom + meta. Une carte dashed "Créer" termine la row.
 */
export function CollectionsSection(): JSX.Element {
  const { data } = useMyCollections();
  const { data: shelfData } = useMyShelf();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<{ open: boolean; existingId: string | null }>({
    open: false,
    existingId: null,
  });

  const collections = data?.items ?? [];
  const allBooks: ReadonlyArray<Book> =
    shelfData?.items.map((e) => e.book) ?? [];
  const booksById = new Map(allBooks.map((b) => [b.id, b]));

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="font-serif text-lg font-semibold text-brown">
          Mes collections
        </h2>
        <p className="font-script text-base text-brown-soft">
          tes étagères thématiques 🌸
        </p>
      </div>

      <div className="collections-row">
        {collections.map((c) => {
          const books = c.bookIds
            .map((id) => booksById.get(id))
            .filter((b): b is Book => b !== undefined);
          return (
            <CollectionCard
              key={c.id}
              name={c.name}
              emoji={c.emoji}
              description={c.description}
              bookCount={c.bookIds.length}
              previewBooks={books.slice(0, 11)}
              onClick={() => navigate(`/collections/${c.id}`)}
            />
          );
        })}
        <CreateCollectionCard
          onClick={() => setEditing({ open: true, existingId: null })}
        />
      </div>

      {editing.open && (
        <CollectionFormModal
          existingId={editing.existingId}
          onClose={() => setEditing({ open: false, existingId: null })}
        />
      )}
    </section>
  );
}

interface CollectionCardProps {
  readonly name: string;
  readonly emoji: string;
  readonly description: string | null;
  readonly bookCount: number;
  readonly previewBooks: ReadonlyArray<Book>;
  readonly onClick: () => void;
}

function CollectionCard({
  name,
  emoji,
  description,
  bookCount,
  previewBooks,
  onClick,
}: CollectionCardProps): JSX.Element {
  // Hauteurs variables des mini-tranches pour donner un effet vivant
  const heights = ['80%', '90%', '75%', '85%', '95%'];
  return (
    <button
      type="button"
      onClick={onClick}
      className="collection-card-button"
      aria-label={`Ouvrir ${name}`}
    >
      <div className="collection-preview">
        {previewBooks.map((b, i) => {
          const [c1, c2] = pickColors(b);
          return (
            <span
              key={b.id}
              className="mini-spine"
              style={{
                height: heights[i % heights.length],
                background: `linear-gradient(180deg, ${c1}, ${c2})`,
              }}
            />
          );
        })}
      </div>
      <div className="mt-2.5 px-1">
        <p className="font-serif text-sm font-semibold text-brown">
          <span className="mr-1.5" aria-hidden>
            {emoji}
          </span>
          {name}
        </p>
        <p className="mt-0.5 truncate font-script text-sm text-brown-soft">
          {bookCount} livre{bookCount > 1 ? 's' : ''}
          {description ? ` · ${description}` : ''}
        </p>
      </div>
    </button>
  );
}

function CreateCollectionCard({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="collection-create-card"
      aria-label="Créer une collection"
    >
      <span className="font-serif text-3xl font-semibold leading-none">+</span>
      <span className="font-script text-sm">
        Créer une
        <br />
        collection
      </span>
    </button>
  );
}
